/* External */
import {
  createContext,
  memo,
  useContext,
  useState,
  useEffect,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import get from 'lodash-es/get';

/* Utils */
import platform from '~/utils/platform';
import { getConfig } from '../../utils/configUtils';
import {
  Country,
  TIME_VALUES,
  MINUTES_AFTER_CONSECUTIVE_EPISODES_VOD,
  WELCOME_PAGE_GENERIC_IMAGE,
  CountryLanguages,
  COUNTRY_TO_REGION_MAP,
  Locale,
  STORAGE_COUNTRY,
} from '../../utils/constants';
import { WEB_URL } from '../../utils/env';

const ConfigContext = createContext({});
const { SECONDS_BY_HOUR, SECONDS_BY_MINUTE, MILLISECONDS_BY_SECOND } = TIME_VALUES;

export function useConfig() {
  return useContext(ConfigContext);
}

export function useConfigProperty(path) {
  const config = useConfig();
  return useMemo(() => get(config, path), [config, path]);
}

export function usePlayerBuffer(isLive) {
  const playerPath = 'player.orbis';
  const {
    liveVideoBufferBackwardDuration,
    liveVideoBufferForwardDuration,
    liveRebufferingGoal,
    vodVideoBufferBackwardDuration,
    vodVideoBufferForwardDuration,
    vodRebufferingGoal,
  } = useConfigProperty(playerPath) || {};

  const liveConfig = useMemo(() => ({
    backward: liveVideoBufferBackwardDuration,
    forward: liveVideoBufferForwardDuration,
    rebufferingGoal: liveRebufferingGoal,
  }), [
    liveRebufferingGoal,
    liveVideoBufferBackwardDuration,
    liveVideoBufferForwardDuration,
  ]);

  const vodConfig = useMemo(() => ({
    backward: vodVideoBufferBackwardDuration,
    forward: vodVideoBufferForwardDuration,
    rebufferingGoal: vodRebufferingGoal,
  }), [
    vodRebufferingGoal,
    vodVideoBufferBackwardDuration,
    vodVideoBufferForwardDuration,
  ]);

  return useMemo(() => {
    if (platform.appName === 'lg_tv') {
      return {
        backward: 0,
        forward: 6,
        rebufferingGoal: 3,
      };
    }
    return isLive ? liveConfig : vodConfig;
  }, [isLive, liveConfig, vodConfig]);
}

export function useCountry() {
  return useConfigProperty('userCountry');
}

export function useIsBrazil() {
  return useCountry() === Country.BRAZIL;
}

export function useRegion() {
  return COUNTRY_TO_REGION_MAP[useCountry()] || '';
}

export function useLanguage() {
  return CountryLanguages[useCountry()] || Locale.ES;
}

export function useRecentlyWatchedLimit() {
  return useConfigProperty('content.miniepg.recentlyWatchedLimit');
}

export function useEnableToShowLockIcon() {
  return useConfigProperty('lockIcon');
}

export function useAreYouStillWatchingInactiveTime(isLive) {
  const liveWatching = useConfigProperty('content.live.liveWatching');
  return isLive ? (
    liveWatching
     * SECONDS_BY_HOUR * MILLISECONDS_BY_SECOND
  )
    : MINUTES_AFTER_CONSECUTIVE_EPISODES_VOD * SECONDS_BY_MINUTE * MILLISECONDS_BY_SECOND;
}

export function useAreYouStillWatchingEpisodeLimit() {
  return useConfigProperty('content.vod.vodWatching');
}

export function useWelcomePageData() {
  const welcomePage = useConfigProperty('welcomePage');
  return {
    ...welcomePage,
    images: welcomePage?.images?.length > 0 ? welcomePage.images.map((item) => ({
      ...item,
      image: item.image && `${WEB_URL}${item.image}`,
    })) : [WELCOME_PAGE_GENERIC_IMAGE],
  };
}

export function useIsFetchingConfig() {
  const config = useConfig();
  return config?.isFetching;
}

export function useBitmovinAnalyticsConfig() {
  return {
    enable: useConfigProperty('player.bitmovinAnalytics.enable'),
    key: useConfigProperty('player.bitmovinAnalytics.key'),
  };
}

export function useLicenseRequestConfig() {
  const retries = useConfigProperty('player.licenseRequest.retries');
  const timeout = useConfigProperty('player.licenseRequest.timeout');
  return useMemo(() => ({
    retries,
    timeout,
  }), [retries, timeout]);
}

function ConfigProvider({ children }) {
  const [config, setConfig] = useState({ isFetching: true });

  useEffect(() => {
    async function fetchAndSetConfig() {
      const fetchedConfig = await getConfig();
      localStorage.setItem(STORAGE_COUNTRY, fetchedConfig.userCountry);
      setConfig({ ...fetchedConfig, isFetching: false });
    }

    fetchAndSetConfig();
  }, []);

  return (
    <ConfigContext.Provider value={config}>
      {children}
    </ConfigContext.Provider>
  );
}

ConfigProvider.propTypes = {
  children: PropTypes.node,
};

export default memo(ConfigProvider);
